import { PlusIcon, TrashIcon } from '@radix-ui/react-icons';
import { useEffect, useMemo, useState } from 'react';

import { Button } from '../../../primitives/button';
import { SelectModel } from '../../select/select-model';
import { ScheduleReference, WorkflowSchedule } from '../../workflow-schedule';
import { ReferenceField } from './components/reference-field';
import { ACTION_TYPE_MODEL, DEFAULT_SCHEDULE_ACTION_VALUE } from './constants';
import { getContentLabel } from './utils/get-content-label';

import { BotIcon } from '../../../svg/bot-icon';
import { CalendarClockIcon } from '../../../svg/calendar-clock-icon';
import { LabelWithoutRef } from '../../label/label-without-ref';
import type {
  ActionType,
  ScheduleActionContent,
  ScheduleActionItem,
  ScheduleActionItemErrors,
  ScheduleActionModule,
  ScheduleActionReferenceMetadata,
  ScheduleActionValue,
  SchedulingMode,
} from './types';

export * from './types';

interface ScheduleActionProps {
  audienceModules: ScheduleActionModule[];
  disabled?: boolean;
  errors?: ScheduleActionItemErrors[];
  getModuleContents: (moduleId: string) => Promise<ScheduleActionContent[]>;
  id?: string;
  schedulingMode?: SchedulingMode;
  reference?: ScheduleReference;
  /** Describe the first previous-based action relative to its trigger, keeping the stored reference. */
  relativeToTrigger?: boolean;
  templateReference?: ScheduleActionReferenceMetadata;
  audienceReference?: ScheduleActionReferenceMetadata;
  templateModules: ScheduleActionModule[];
  value?: ScheduleActionValue | null;
  onChange: (value: ScheduleActionValue) => void;
}

export function ScheduleAction({
  audienceModules,
  audienceReference,
  disabled = false,
  errors,
  getModuleContents,
  id = 'schedule-action',
  onChange,
  reference = 'now',
  relativeToTrigger = false,
  schedulingMode,
  templateModules,
  templateReference,
  value,
}: ScheduleActionProps) {
  const current = useMemo<ScheduleActionValue>(
    () =>
      value ?? {
        ...DEFAULT_SCHEDULE_ACTION_VALUE,
        actions: [
          {
            ...DEFAULT_SCHEDULE_ACTION_VALUE.actions[0],
            templateReference: { moduleId: templateReference?.moduleId ?? '', contentId: '' },
            audienceReference: { moduleId: audienceReference?.moduleId ?? '', contentId: '' },
          },
        ],
        schedule: {
          ...DEFAULT_SCHEDULE_ACTION_VALUE.schedule,
          mode: schedulingMode ?? DEFAULT_SCHEDULE_ACTION_VALUE.schedule.mode,
        },
      },
    [value, schedulingMode, templateReference?.moduleId, audienceReference?.moduleId],
  );
  const contentsByModule = useModuleContents(
    current.actions.flatMap(({ audienceReference, templateReference }) => [
      audienceReference.moduleId,
      templateReference.moduleId,
    ]),
    getModuleContents,
  );
  const incompleteActions =
    errors
      ?.map((error, index) => (hasReferenceErrors(error) ? index + 1 : undefined))
      .filter((index): index is number => index !== undefined) ?? [];

  useEffect(() => {
    if (!value) {
      onChange(current);
    }
  }, [current, onChange, value]);

  const updateAction = (index: number, nextAction: ScheduleActionItem) => {
    onChange({
      ...current,
      actions: current.actions.map((action, actionIndex) =>
        actionIndex === index ? nextAction : action,
      ),
    });
  };

  const addAction = () => {
    onChange({
      ...current,
      actions: [
        ...current.actions,
        {
          ...DEFAULT_SCHEDULE_ACTION_VALUE.actions[0],
          templateReference: { moduleId: templateReference?.moduleId ?? '', contentId: '' },
          audienceReference: { moduleId: audienceReference?.moduleId ?? '', contentId: '' },
        },
      ],
    });
  };

  const removeAction = (index: number) => {
    onChange({
      ...current,
      actions: current.actions.filter((_, actionIndex) => actionIndex !== index),
    });
  };

  return (
    <WorkflowSchedule
      accordionChildren={{
        content: (
          <div className="grid gap-6">
            <IncompleteActionsMessage actions={incompleteActions} />
            {current.actions.map((action, index) => (
              <div className="grid gap-4 border-b pb-6" key={index}>
                <div className="flex items-center gap-2">
                  <BotIcon className="size-5 text-chart-3" />
                  <LabelWithoutRef className="p-0 pt-1 text-xs font-medium text-chart-3">
                    Acción {index + 1}
                  </LabelWithoutRef>
                  <Button
                    aria-label={`Eliminar acción ${index + 1}`}
                    className="ml-auto opacity-50"
                    disabled={disabled || current.actions.length === 1}
                    onClick={() => removeAction(index)}
                    size="icon-sm"
                    title={`Eliminar acción ${index + 1}`}
                    variant="destructive"
                  >
                    <TrashIcon />
                  </Button>
                </div>
                <div className="space-y-1">
                  <LabelWithoutRef className="pt-0 text-xs font-medium text-muted-foreground">
                    Tipo
                  </LabelWithoutRef>
                  <SelectModel
                    disabled={disabled}
                    id={`${id}-action-${index}-type`}
                    model={ACTION_TYPE_MODEL}
                    value={action.type}
                    onValueChange={(type) =>
                      updateAction(index, { ...action, type: type as ActionType })
                    }
                  />
                </div>

                <ReferenceField
                  contents={contentsByModule[action.templateReference.moduleId] ?? []}
                  disabled={disabled}
                  errors={errors?.[index]?.templateReference}
                  id={`${id}-action-${index}-template-reference`}
                  label="Plantilla"
                  isModuleEditable={
                    templateReference?.isEditable !== false || !action.templateReference.moduleId
                  }
                  modules={templateModules}
                  value={action.templateReference}
                  onChange={(templateReference) =>
                    updateAction(index, { ...action, templateReference })
                  }
                />

                <ReferenceField
                  contents={contentsByModule[action.audienceReference.moduleId] ?? []}
                  disabled={disabled}
                  errors={errors?.[index]?.audienceReference}
                  id={`${id}-action-${index}-audience-reference`}
                  label="Audiencia"
                  isModuleEditable={
                    audienceReference?.isEditable !== false || !action.audienceReference.moduleId
                  }
                  modules={audienceModules}
                  value={action.audienceReference}
                  onChange={(audienceReference) =>
                    updateAction(index, { ...action, audienceReference })
                  }
                />
              </div>
            ))}
            <Button
              title="Agregar acción"
              aria-label="Agregar acción"
              disabled={disabled}
              onClick={addAction}
              size="icon"
              variant="outline"
            >
              <PlusIcon />
            </Button>
            <div className="flex items-center gap-2 text-chart-3">
              <CalendarClockIcon className="size-5" />
              <LabelWithoutRef className="p-0 pt-1 text-xs font-medium">
                Programación
              </LabelWithoutRef>
            </div>
          </div>
        ),
        trigger: (
          <div className="grid gap-4">
            <IncompleteActionsMessage actions={incompleteActions} />
            {current.actions.map((action, index) => (
              <div className="grid gap-3" key={index}>
                <div className="flex items-center gap-2 text-chart-3">
                  <BotIcon className="size-5" />
                  <LabelWithoutRef className="p-0 pt-1 text-xs font-medium">
                    Acción {index + 1}
                  </LabelWithoutRef>
                </div>
                <TriggerSection
                  title="Tipo"
                  value={ACTION_TYPE_MODEL.find(({ value }) => value === action.type)?.item}
                />
                <TriggerSection
                  title="Plantilla"
                  value={getReferenceLabel(
                    action.templateReference,
                    templateModules,
                    contentsByModule[action.templateReference.moduleId] ?? [],
                  )}
                />
                <TriggerSection
                  title="Audiencia"
                  value={getReferenceLabel(
                    action.audienceReference,
                    audienceModules,
                    contentsByModule[action.audienceReference.moduleId] ?? [],
                  )}
                />
              </div>
            ))}

            <div className="mb-3 flex items-center gap-2 text-chart-3">
              <CalendarClockIcon className="size-5" />
              <LabelWithoutRef className="p-0 pt-1 text-xs font-medium">
                Programación
              </LabelWithoutRef>
            </div>
          </div>
        ),
      }}
      className={{
        content: 'p-8',
        trigger: 'p-8',
      }}
      disabled={disabled}
      id={`${id}-schedule`}
      reference={reference}
      relativeToTrigger={relativeToTrigger}
      value={current.schedule}
      onChange={(schedule) =>
        onChange({ ...current, schedule: { ...schedule, mode: current.schedule.mode } })
      }
    />
  );
}

function hasReferenceErrors(error?: ScheduleActionItemErrors) {
  return Boolean(
    error?.templateReference?.moduleId ||
    error?.templateReference?.contentId ||
    error?.audienceReference?.moduleId ||
    error?.audienceReference?.contentId,
  );
}

function IncompleteActionsMessage({ actions }: { actions: number[] }) {
  if (actions.length === 0) return null;

  return (
    <p className="text-sm font-medium text-destructive">
      La acción "{actions.join(', ')}" está incompleta.
    </p>
  );
}

function TriggerSection({ title, value }: { title: string; value?: string }) {
  return (
    <div>
      <LabelWithoutRef className="p-0 text-xs font-medium text-muted-foreground">
        {title}
      </LabelWithoutRef>
      {value ? (
        <p className="truncate text-sm">{value}</p>
      ) : (
        <p className="text-sm text-destructive/80">No seleccionado</p>
      )}
    </div>
  );
}

function getReferenceLabel(
  reference: ScheduleActionValue['actions'][number]['templateReference'],
  modules: ScheduleActionModule[],
  contents: ScheduleActionContent[],
) {
  const moduleLabel = modules.find(({ id }) => id === reference.moduleId)?.title;
  const selectedContent = contents.find(({ id }) => String(id) === reference.contentId);
  const contentLabel = selectedContent ? getContentLabel(selectedContent) : reference.contentId;

  return [moduleLabel, contentLabel].filter(Boolean).join(' · ') || undefined;
}

function useModuleContents(
  moduleIds: string[],
  getModuleContents: (moduleId: string) => Promise<ScheduleActionContent[]>,
) {
  const uniqueModuleIds = [...new Set(moduleIds.filter(Boolean))].sort();
  const moduleIdsKey = uniqueModuleIds.join('\u0000');
  const [contents, setContents] = useState<Record<string, ScheduleActionContent[]>>({});

  useEffect(() => {
    let active = true;

    setContents({});
    Promise.all(
      uniqueModuleIds.map(async (moduleId) => {
        try {
          return [moduleId, await getModuleContents(moduleId)] as const;
        } catch {
          return [moduleId, []] as const;
        }
      }),
    ).then((entries) => {
      const entriesWithContents = entries.filter(([, items]) => items.length > 0);
      if (active && entriesWithContents.length > 0) {
        setContents(Object.fromEntries(entriesWithContents));
      }
    });

    return () => {
      active = false;
    };
  }, [getModuleContents, moduleIdsKey]);

  return contents;
}
